import { money, isReported, KindTag, FigureNote, Notes, NotAvailable } from "./IntlBits";

function Cell({ f, code, testId, country }) {
  return (
    <td data-testid={testId} className="px-3 py-3 align-top">
      {country && <span className="block font-mono text-[10px] uppercase tracking-wider text-stone-500">{country}</span>}
      {isReported(f) ? (
        <>
          <span className="font-editorial text-xl text-stone-900">{money(f.value, f.unit)}</span>
          {f.p25 != null && f.p75 != null && <span className="block text-[11px] text-stone-500">Middle half: {money(f.p25, f.unit)} to {money(f.p75, f.unit)}</span>}
          <span className="block text-[11px] text-stone-500">{f.label} ({code}) · {f.period}</span>
        </>
      ) : (
        <><NotAvailable testId={`${testId}-na`} /><span className="mt-1 block text-[11px] text-stone-500">{code}</span></>
      )}
    </td>
  );
}

const Mapping = ({ o, testId }) => (
  <span data-testid={testId} className={`mt-1 block text-[11px] ${o.mapping === "equivalent" ? "text-emerald-700" : "text-amber-700"}`}>
    {o.mapping === "equivalent" ? "Same occupation in both countries" : `Approximate match: ${o.note}`}
  </span>
);

function OccNotes({ d }) {
  const b = d.baseline;
  const items = d.occupations.flatMap((o) => [[o, "uk", o.uk, `SOC 2020 ${o.uk_code}`], [o, "us", o.us, `SOC ${o.us_code}`]]);
  return (
    <Notes testId="sci-intl-occ-notes">
      <ul className="space-y-1.5">
        {items.map(([o, k, f, code]) => (f
          ? <FigureNote key={`${o.key}-${k}`} f={f} sources={d.sources} extra={code} testId={`sci-intl-occ-${o.key}-${k}-source`} />
          : <li key={`${o.key}-${k}`} data-testid={`sci-intl-occ-${o.key}-${k}-source`}><span className="font-medium text-stone-800">{o.label} ({code}): </span>Not available. No official figure published for this code.</li>))}
      </ul>
      <p data-testid="sci-intl-mapping-status">{d.mapping_status}. UK baseline: {b.UK?.population} ({b.UK?.period}); US baseline: {b.US?.population} ({b.US?.period}).</p>
    </Notes>
  );
}

export function OccupationPanel({ d }) {
  const b = d.baseline;
  return (
    <div data-testid="sci-intl-occupations">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <KindTag kind="occupation_wide_wage" testId="sci-intl-occ-kind" />
        <p className="text-xs text-stone-600">Pay of everyone in the job, of any age, experience or degree subject. Not what {d.subject.name} graduates earn. Pounds and dollars are never converted.</p>
      </div>
      <div className="space-y-3 md:hidden" data-testid="sci-intl-occ-mobile">
        {d.occupations.map((o) => (
          <div key={o.key} className="rounded-xl border border-stone-200 bg-white p-4" data-testid={`sci-intl-occ-m-${o.key}`}>
            <p className="text-stone-900">{o.label}</p>
            <Mapping o={o} testId={`sci-intl-occ-m-${o.key}-mapping`} />
            <table className="mt-2 w-full text-sm"><tbody>
              <tr><Cell f={o.uk} code={`SOC 2020 ${o.uk_code}`} testId={`sci-intl-occ-m-${o.key}-uk`} country="United Kingdom · median, full-time (£)" /></tr>
              <tr><Cell f={o.us} code={`SOC ${o.us_code}`} testId={`sci-intl-occ-m-${o.key}-us`} country="United States · median ($)" /></tr>
            </tbody></table>
          </div>
        ))}
        <p className="text-xs text-stone-600" data-testid="sci-intl-occ-m-baseline">For context, all jobs: {money(b.UK?.value, "GBP")} UK (full-time employees) · {money(b.US?.value, "USD")} US (all occupations)</p>
      </div>
      <div className="hidden overflow-x-auto rounded-xl border border-stone-200 bg-white md:block">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-stone-200 bg-stone-50 text-xs text-stone-600">
            <tr><th className="px-3 py-2">Occupation</th><th className="px-3 py-2">UK median, full-time (£)</th><th className="px-3 py-2">US median ($)</th></tr>
          </thead>
          <tbody>
            {d.occupations.map((o) => (
              <tr key={o.key} className="border-b border-stone-100" data-testid={`sci-intl-occ-${o.key}`}>
                <td className="px-3 py-3 align-top">
                  <span className="text-stone-900">{o.label}</span>
                  <Mapping o={o} testId={`sci-intl-occ-${o.key}-mapping`} />
                </td>
                <Cell f={o.uk} code={`SOC 2020 ${o.uk_code}`} testId={`sci-intl-occ-${o.key}-uk`} />
                <Cell f={o.us} code={`SOC ${o.us_code}`} testId={`sci-intl-occ-${o.key}-us`} />
              </tr>
            ))}
            <tr className="bg-stone-50 text-xs text-stone-600" data-testid="sci-intl-occ-baseline">
              <td className="px-3 py-2">For context: all jobs</td>
              <td className="px-3 py-2">{money(b.UK?.value, "GBP")} (all full-time employees)</td>
              <td className="px-3 py-2">{money(b.US?.value, "USD")} (all occupations)</td>
            </tr>
          </tbody>
        </table>
      </div>
      <OccNotes d={d} />
    </div>
  );
}
